/**
 * @fileoverview Authentication Form Layout Component
 * @active Used in multiple authentication pages
 *
 * This component provides a consistent layout for authentication forms,
 * including signup, login, password reset, and verification workflows.
 *
 * @see {@link /src/components/auth/SignIn.tsx} - Login form using this layout
 * @see {@link /src/components/auth/SignUp.tsx} - Signup form using this layout
 * @see {@link /src/layouts/auth/AuthWrapper.tsx} - Parent auth wrapper used with this layout
 * @see {@link /src/layouts/auth/OnboardingWrapper.tsx} - Related onboarding wrapper component
 */

import { type ReactNode } from "react";
import { Flex } from "@chakra-ui/react";
import Link from "next/link";

import LogoDark from "@/assets/svg/logo-dark.svg";

/**
 * Props for the FormLayout component
 */
interface FormLayoutProps {
  /** Child components to render inside the form layout */
  children: ReactNode;
  /** Whether the layout contains message elements that require special styling */
  isMessages?: boolean;
}

/**
 * Form layout component for authentication pages
 *
 * Provides a consistent container with the app logo and styling
 * for all authentication related forms.
 *
 * @param {FormLayoutProps} props - Component props
 * @returns {JSX.Element} The rendered form layout
 */
const FormLayout = ({
  children,
  isMessages = false,
}: FormLayoutProps): JSX.Element => {
  return (
    <Flex
      flexDir={"column"}
      p={"20px 20px"}
      bg={"dashboardBackground"}
      w={{ base: "320px", sm: isMessages ? "333.5px" : "400px" }}
      borderRadius={"8px"}
      boxShadow={"0px 0px 14px 0px rgba(114, 114, 114, 0.25)"}
    >
      <Flex
        alignItems={"center"}
        justifyContent={"center"}
        w={"100%"}
        maxW={"400px"}
        mb={"24px"}
      >
        <Link href={"/"} title="Home">
          <LogoDark />
        </Link>
      </Flex>
      {children}
    </Flex>
  );
};

export default FormLayout;
